import type { UsageRecord } from "./parser.ts";

export type RangeKey = "day" | "week" | "month";

export interface BucketTotals {
  input: number;
  output: number;
  cacheRead: number;
  cacheWrite: number;
  reasoning: number;
  totalTokens: number;
  cost: number;
  requests: number;
}

export interface TimeBucket extends BucketTotals {
  /** Bucket start, epoch ms (local-time aligned). */
  start: number;
  label: string;
}

export interface ModelUsage extends BucketTotals {
  provider: string;
  model: string;
  /** provider/model 组合名，便于展示。 */
  name: string;
  sessions: number;
}

export interface FolderUsage extends BucketTotals {
  /** Working directory path. */
  cwd: string;
  /** Short display name (last segment or full path). */
  name: string;
  sessions: number;
}

export interface Overview {
  range: RangeKey;
  windowStart: number;
  generatedAt: number;
  summary: BucketTotals & { sessions: number };
  buckets: TimeBucket[];
  models: ModelUsage[];
  folders: FolderUsage[];
}

export const RANGES: Record<
  RangeKey,
  { bucketMs: number; bucketCount: number; unit: "hour" | "day" }
> = {
  day: { bucketMs: 60 * 60 * 1000, bucketCount: 24, unit: "hour" },
  week: { bucketMs: 24 * 60 * 60 * 1000, bucketCount: 7, unit: "day" },
  month: { bucketMs: 24 * 60 * 60 * 1000, bucketCount: 30, unit: "day" },
};

export function isRangeKey(value: string): value is RangeKey {
  return value === "day" || value === "week" || value === "month";
}

/** Align a timestamp down to the start of its local hour or local day. */
function align(timestamp: number, unit: "hour" | "day"): number {
  const date = new Date(timestamp);
  if (unit === "hour") {
    date.setMinutes(0, 0, 0);
  } else {
    date.setHours(0, 0, 0, 0);
  }
  return date.getTime();
}

function pad2(value: number): string {
  return value < 10 ? `0${value}` : String(value);
}

function bucketLabel(start: number, unit: "hour" | "day"): string {
  const date = new Date(start);
  if (unit === "hour") return `${pad2(date.getHours())}:00`;
  return `${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

function emptyTotals(): BucketTotals {
  return {
    input: 0,
    output: 0,
    cacheRead: 0,
    cacheWrite: 0,
    reasoning: 0,
    totalTokens: 0,
    cost: 0,
    requests: 0,
  };
}

function addRecord(totals: BucketTotals, record: UsageRecord): void {
  totals.input += record.input;
  totals.output += record.output;
  totals.cacheRead += record.cacheRead;
  totals.cacheWrite += record.cacheWrite;
  totals.reasoning += record.reasoning;
  totals.totalTokens += record.totalTokens;
  totals.cost += record.cost;
  totals.requests += 1;
}

/**
 * Aggregate usage records into a fixed grid of time buckets plus a
 * per-model ranking, for the given range ending at `now`.
 */
export function aggregate(
  records: UsageRecord[],
  range: RangeKey,
  now: number = Date.now(),
): Overview {
  const config = RANGES[range];
  const lastBucketStart = align(now, config.unit);
  const windowStart = lastBucketStart - (config.bucketCount - 1) * config.bucketMs;
  const windowEnd = lastBucketStart + config.bucketMs;

  const buckets: TimeBucket[] = [];
  for (let i = 0; i < config.bucketCount; i += 1) {
    const start = windowStart + i * config.bucketMs;
    buckets.push({ start, label: bucketLabel(start, config.unit), ...emptyTotals() });
  }

  const summary = { ...emptyTotals(), sessions: 0 };
  const summarySessions = new Set<string>();
  const byModel = new Map<string, ModelUsage & { sessionIds: Set<string> }>();
  const byFolder = new Map<string, FolderUsage & { sessionIds: Set<string> }>();

  for (const record of records) {
    if (record.timestamp < windowStart || record.timestamp >= windowEnd) continue;

    const bucketIndex = Math.floor((record.timestamp - windowStart) / config.bucketMs);
    addRecord(buckets[bucketIndex], record);
    addRecord(summary, record);
    summarySessions.add(record.sessionId);

    const name = `${record.provider}/${record.model}`;
    let model = byModel.get(name);
    if (!model) {
      model = {
        provider: record.provider,
        model: record.model,
        name,
        ...emptyTotals(),
        sessions: 0,
        sessionIds: new Set<string>(),
      };
      byModel.set(name, model);
    }
    addRecord(model, record);
    model.sessionIds.add(record.sessionId);

    // Folder aggregation
    const folderCwd = record.cwd || "未知目录";
    let folder = byFolder.get(folderCwd);
    if (!folder) {
      folder = {
        cwd: folderCwd,
        name: folderCwd === "未知目录" ? folderCwd : (folderCwd.split("/").pop() || folderCwd),
        ...emptyTotals(),
        sessions: 0,
        sessionIds: new Set<string>(),
      };
      byFolder.set(folderCwd, folder);
    }
    addRecord(folder, record);
    folder.sessionIds.add(record.sessionId);
  }

  summary.sessions = summarySessions.size;
  const models = [...byModel.values()]
    .map(({ sessionIds, ...rest }) => ({ ...rest, sessions: sessionIds.size }))
    .sort((a, b) => b.totalTokens - a.totalTokens);

  const folders = [...byFolder.values()]
    .map(({ sessionIds, ...rest }) => ({ ...rest, sessions: sessionIds.size }))
    .sort((a, b) => b.totalTokens - a.totalTokens);

  return {
    range,
    windowStart,
    generatedAt: now,
    summary,
    buckets,
    models,
    folders,
  };
}
